import { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";

/**
 * LB (pay-do-stable) historically returned the full Firestore purchase document.
 * Checkout expects the slimmer api-payments-style payload (top-level `funnel`, etc.).
 */
function normalizePurchasePollPayload(data) {
  if (!data || typeof data !== "object") return data;
  // Full Firestore purchase document (e.g. pay-do-stable GET /purchases/:id before normalization)
  const isRawFirestorePurchase =
    data.sellerId != null &&
    data.customer &&
    data.purchase &&
    data.status != null &&
    data.product;

  if (isRawFirestorePurchase) {
    const nested = data.purchase;
    return {
      status: data.status,
      status_date: data.status_date,
      status_description: data.status_description,
      status_reason: data.status_reason,
      createdAt: data.createdAt,
      pix: data.pix,
      paymentMethod: nested?.paymentMethod,
      purchase: nested
        ? {
            funnelId: nested.funnelId ?? null,
            paymentMethod: nested.paymentMethod,
          }
        : null,
      funnel: data.funnel ?? null,
      receiptToken: data.receiptToken ?? null,
      id: data.id || nested?.id,
    };
  }
  return data;
}

/**
 * @param {string|null|undefined} checkoutFunnelId - Funnel id from checkout init; used when GET /purchases omits nested purchase so we still wait for webhook-written `funnel`.
 */
export const usePurchaseStatus = (purchaseId, apiUrl, checkoutFunnelId = null) => {
  const [purchaseData, setPurchaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchCount, setFetchCount] = useState(0);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const intervalIdRef = useRef(null);

  useEffect(() => {
    // Early returns for invalid states
    if (!purchaseId || purchaseId === "error") {
      setLoading(false);
      return;
    }

    // Reset state for new purchase
    setPurchaseData(null);
    setError(null);
    setFetchCount(0);
    setLoading(true);

    let isActive = true;
    let currentFetchCount = 0;
    let currentPurchaseData = null;
    let postApprovalFunnelPolls = 0;
    const maxPostApprovalFunnelPolls = 20;

    const fetchPurchase = async () => {
      // Skip if component is unmounted
      if (!isActive) return;

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      try {
        const response = await fetch(`${apiUrl}/purchases/${purchaseId}`, {
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch purchase: ${response.status}`);
        }

        const raw = await response.json();
        const data = normalizePurchasePollPayload(raw);

        if (!isActive) return; // Component unmounted during fetch

        // Handle PIX timeout
        if (data.status === "initiated" && data.paymentMethod === "pix") {
          const timeSinceCreation = Date.now() - data.createdAt;
          if (timeSinceCreation > 10000) {
            // 10 seconds
            setError({
              code: "pix-generation-failed",
              message: "Não foi possível gerar o pix. Tente novamente.",
            });
            setLoading(false);
            // Clear interval on error
            if (intervalIdRef.current) {
              clearTimeout(intervalIdRef.current);
              intervalIdRef.current = null;
            }
            return;
          }
        }

        currentPurchaseData = data;
        currentFetchCount += 1;

        setPurchaseData(data);
        setFetchCount(currentFetchCount);
        setError(null);

        // Post-checkout funnel upsell: webhook may add purchases.funnel after status is approved.
        const funnelExpected =
          Boolean(data.purchase?.funnelId) || Boolean(checkoutFunnelId);
        const awaitingFunnelSession =
          (data.status === "approved" || data.status === "trial_started") &&
          funnelExpected &&
          !data.funnel &&
          postApprovalFunnelPolls < maxPostApprovalFunnelPolls;

        if (awaitingFunnelSession) {
          postApprovalFunnelPolls += 1;
          if (intervalIdRef.current) {
            clearTimeout(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          intervalIdRef.current = setTimeout(() => {
            if (isActive) fetchPurchase();
          }, 1000);
          return;
        }

        // Stop polling if purchase is complete
        if (
          [
            "approved",
            "declined",
            "failed",
            "trial_setup_error",
            "trial_started",
          ].includes(data.status)
        ) {
          if (intervalIdRef.current) {
            clearTimeout(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          return;
        }

        // Update polling interval based on new state
        scheduleNextPoll();
      } catch (err) {
        if (!isActive) return;

        if (err.name !== "AbortError") {
          Sentry.captureException(err, {
            extra: {
              purchaseId,
              apiUrl,
              fetchCount: currentFetchCount,
              message: err.message,
            },
          });
          setError({
            code: "fetch-error",
            message: err.message,
          });
          // Stop polling on error
          if (intervalIdRef.current) {
            clearTimeout(intervalIdRef.current);
            intervalIdRef.current = null;
          }
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    const scheduleNextPoll = () => {
      // Clear existing interval
      if (intervalIdRef.current) {
        clearTimeout(intervalIdRef.current);
      }

      if (!isActive) return;

      // Calculate next interval based on current state
      let interval = 5000; // default

      if (!currentPurchaseData) {
        interval = 3000; // Initial fast polling
      } else if (currentPurchaseData.status === "initiated") {
        interval = currentFetchCount < 3 ? 2000 : 3000;
      } else if (currentPurchaseData.status === "pix_generated") {
        if (currentFetchCount < 3) interval = 5000;
        else if (currentFetchCount < 10) interval = 10000;
        else interval = 15000;
      }

      intervalIdRef.current = setTimeout(() => {
        if (isActive) {
          fetchPurchase();
        }
      }, interval);
    };

    // Initial fetch
    fetchPurchase();

    // Cleanup
    return () => {
      isActive = false;
      if (intervalIdRef.current) {
        clearTimeout(intervalIdRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [purchaseId, apiUrl, checkoutFunnelId]);

  return { purchaseData, loading, error, fetchCount };
};
